// Container-deployment hand-holding for gateways (docs/GatewaySelfUpdate.md +
// the gateway list page). The registry host/image come from the backend; the
// pull credential is the gateway provisioning token itself
// (`docker login -u gateway-token -p <token>`). SaaS-only — the endpoint 503s
// on the trial edition, so callers hide the section when this errors.
import axios from 'axios';
import { config } from '@/config.ts';

const backendApi = config.backendApi;

export interface GatewayDeploymentInfo {
  registry: string;
  repository: string;
  /** `<registry>/<repository>:latest` */
  image: string;
  /** Basic-auth username paired with a provisioning token as the password. */
  loginUsername: string;
}

export const fetchGatewayDeploymentInfo = async (): Promise<GatewayDeploymentInfo | null> => {
  try {
    const response = await axios.get<GatewayDeploymentInfo>(
      `${backendApi}/api/registry/gateway-deployment`,
      { withCredentials: true },
    );
    return response.data;
  } catch {
    // 503 on editions without a configured registry — treat as "unavailable".
    return null;
  }
};

const K8S_PULL_SECRET = 'witina-gateway-pull';
const CONTAINER_NAME = 'witina-gateway';

/** `docker login` line. The token is the password. */
export function dockerLoginCommand(info: GatewayDeploymentInfo, token: string): string {
  return `docker login ${info.registry} -u ${info.loginUsername} -p ${token}`;
}

/**
 * `docker run` line. Host networking + the three network capabilities the
 * launcher raises as ambient caps (raise_ambient_caps): NET_ADMIN for
 * promiscuous-mode LLDP/CDP sniffing, NET_RAW for passive packet capture
 * (AF_PACKET), and NET_BIND_SERVICE for the TFTP server on UDP 69. Each must
 * be in the container's bounding set or the launcher degrades that feature
 * *silently* (log warning only, no failure). We add all three explicitly even
 * though stock Docker's default bounding set already carries NET_RAW +
 * NET_BIND_SERVICE (only NET_ADMIN is absent): Podman — a common `docker`
 * alias on RHEL/Fedora — dropped NET_RAW from its defaults, so listing just
 * NET_ADMIN there would break passive discovery. Keep in sync with the
 * compose/k8s snippets. A named volume for /data so updates and identity
 * persist across restarts, a restart policy (required so the self-update
 * exit(66) relaunches into the new version), and self-update enabled.
 */
export function dockerRunCommand(info: GatewayDeploymentInfo, token: string): string {
  return [
    'docker run -d \\',
    `  --name ${CONTAINER_NAME} \\`,
    '  --restart unless-stopped \\',
    '  --network host \\',
    '  --cap-add NET_ADMIN \\',
    '  --cap-add NET_RAW \\',
    '  --cap-add NET_BIND_SERVICE \\',
    `  -v ${CONTAINER_NAME}-data:/data \\`,
    '  -e GATEWAY_SELF_UPDATE=true \\',
    `  ${info.image} \\`,
    `  --provision-token ${token}`,
  ].join('\n');
}

/**
 * docker-compose.yml. Compose authenticates via the credentials `docker
 * login` stores in ~/.docker/config.json, so the login step runs first (same
 * command as the Docker tab). cap_add carries the same three network caps as
 * the docker run / k8s snippets — see dockerRunCommand for why all three are
 * required.
 */
export function dockerComposeFile(info: GatewayDeploymentInfo, token: string): string {
  return `services:
  gateway:
    image: ${info.image}
    container_name: ${CONTAINER_NAME}
    restart: unless-stopped
    network_mode: host
    cap_add:
      - NET_ADMIN
      - NET_RAW
      - NET_BIND_SERVICE
    environment:
      GATEWAY_SELF_UPDATE: "true"
    command: ["--provision-token", "${token}"]
    volumes:
      - gateway-data:/data
volumes:
  gateway-data:
`;
}

/**
 * The `kubectl` one-liner that creates the image-pull secret. Unlike docker,
 * Kubernetes can't reuse a host login — it needs its own registry secret, and
 * it re-pulls on every reschedule, which is why the token stays valid after
 * the gateway is claimed.
 */
export function k8sPullSecretCommand(info: GatewayDeploymentInfo, token: string, namespace: string): string {
  return [
    'kubectl create secret docker-registry ' + K8S_PULL_SECRET + ' \\',
    `  --namespace ${namespace} \\`,
    `  --docker-server=${info.registry} \\`,
    `  --docker-username=${info.loginUsername} \\`,
    `  --docker-password=${token}`,
  ].join('\n');
}

/**
 * Kubernetes Deployment. hostNetwork + NET_ADMIN/NET_RAW for capture;
 * allowPrivilegeEscalation MUST stay true — the launcher relies on file
 * capabilities, which K8s blocks when it's false (docs/GatewayNonRoot.md).
 * A PVC persists /data. The image-pull secret is created by the command above.
 */
export function k8sDeployment(info: GatewayDeploymentInfo, token: string, namespace: string): string {
  return `apiVersion: v1
kind: PersistentVolumeClaim
metadata:
  name: ${CONTAINER_NAME}-data
  namespace: ${namespace}
spec:
  accessModes: ["ReadWriteOnce"]
  resources:
    requests:
      storage: 2Gi
---
apiVersion: apps/v1
kind: Deployment
metadata:
  name: ${CONTAINER_NAME}
  namespace: ${namespace}
spec:
  replicas: 1
  strategy:
    type: Recreate
  selector:
    matchLabels: { app: ${CONTAINER_NAME} }
  template:
    metadata:
      labels: { app: ${CONTAINER_NAME} }
    spec:
      hostNetwork: true
      dnsPolicy: ClusterFirstWithHostNet
      imagePullSecrets:
        - name: ${K8S_PULL_SECRET}
      containers:
        - name: gateway
          image: ${info.image}
          args: ["--provision-token", "${token}"]
          env:
            - name: GATEWAY_SELF_UPDATE
              value: "true"
          securityContext:
            # Required: the launcher raises file capabilities as ambient caps.
            # Setting this false blocks file caps and breaks LLDP capture.
            allowPrivilegeEscalation: true
            capabilities:
              add: ["NET_ADMIN", "NET_RAW", "NET_BIND_SERVICE"]
          volumeMounts:
            - name: data
              mountPath: /data
      volumes:
        - name: data
          persistentVolumeClaim:
            claimName: ${CONTAINER_NAME}-data
`;
}

/** A signed link the Hyper-V installer downloads the gateway image with. */
export interface HyperVInstallerLink {
  link: string;
  /** Where the installer script is served. */
  scriptUrl: string;
  expiresAt: string;
}

/**
 * Ask for a Hyper-V installer link. It carries who made it and lasts 3 days,
 * like the provisioning token issued beside it. Null when the server issues
 * none (no link key configured), so callers leave the option out.
 */
export const createHyperVInstallerLink = async (): Promise<HyperVInstallerLink | null> => {
  try {
    const response = await axios.post<HyperVInstallerLink>(
      `${backendApi}/api/installers/hyperv/links`,
      {},
      { withCredentials: true },
    );
    return response.data;
  } catch {
    return null;
  }
};

/**
 * The one-liner to run in an elevated PowerShell on the Hyper-V host. TLS 1.2
 * is turned on first: Windows PowerShell 5.1 on older hosts offers only TLS
 * 1.0, and could not fetch the script without it. The link and token are
 * URL-safe and never contain a quote.
 */
export function hyperVInstallCommand(link: HyperVInstallerLink, token: string): string {
  return (
    '[Net.ServicePointManager]::SecurityProtocol = [Net.ServicePointManager]::SecurityProtocol -bor 3072; ' +
    `& ([scriptblock]::Create((irm '${link.scriptUrl}'))) -Link '${link.link}' -Token '${token}'`
  );
}
